import { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';

interface FileWithProgress {
  file: File;
  progress: number;
  uploaded: boolean;
}

interface FileUploaderProps {
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
  maxSize?: number; // in bytes
  initialFiles?: File[]; // Files to display on mount
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export default function FileUploader({ onFilesChange, maxFiles = 10, maxSize = 10 * 1024 * 1024, initialFiles = [] }: FileUploaderProps) {
  const [filesWithProgress, setFilesWithProgress] = useState<FileWithProgress[]>([]);

  // Initialize with any existing files from parent
  useEffect(() => {
    if (initialFiles.length > 0 && filesWithProgress.length === 0) {
      const initial = initialFiles.map(file => ({
        file,
        progress: 100,
        uploaded: true
      }));
      setFilesWithProgress(initial);
    }
  }, [initialFiles]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      progress: 100, // Instant since we're not actually uploading yet
      uploaded: true
    }));

    setFilesWithProgress(prev => [...prev, ...newFiles]);
    onFilesChange([...filesWithProgress.map(f => f.file), ...acceptedFiles]);
  }, [filesWithProgress, onFilesChange]);

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    maxFiles,
    maxSize,
    multiple: true
  });

  const removeFile = (index: number) => {
    const newFiles = filesWithProgress.filter((_, i) => i !== index);
    setFilesWithProgress(newFiles);
    onFilesChange(newFiles.map(f => f.file));
  };

  return (
    <div className="space-y-4">
      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-black bg-gray-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input {...getInputProps()} />
        <div className="space-y-2">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
            aria-hidden="true"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className="text-sm text-gray-600">
            {isDragActive ? (
              <p className="font-medium">Drop files here...</p>
            ) : (
              <>
                <p className="font-medium">Drag & drop files here, or click to select</p>
                <p className="text-xs text-gray-500 mt-1">
                  Up to {maxFiles} files, max {formatFileSize(maxSize)} each
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* File Rejections */}
      {fileRejections.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm font-medium text-red-800 mb-2">Some files were rejected:</p>
          <ul className="text-xs text-red-600 space-y-1">
            {fileRejections.map(({ file, errors }) => (
              <li key={file.name}>
                {file.name}: {errors.map(e => e.message).join(', ')}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Files List */}
      {filesWithProgress.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">
            {filesWithProgress.length} file{filesWithProgress.length > 1 ? 's' : ''} selected
          </p>
          <div className="space-y-2">
            {filesWithProgress.map((fileWithProgress, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-md"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <svg
                      className="h-5 w-5 text-gray-400 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {fileWithProgress.file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(fileWithProgress.file.size)}
                      </p>
                    </div>
                  </div>
                  {fileWithProgress.uploaded && (
                    <div className="mt-2">
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-green-600 h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${fileWithProgress.progress}%` }}
                          />
                        </div>
                        <span className="text-xs text-green-600 font-medium">
                          ✓
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="ml-4 text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
